/**
 * Phase 10 R3 — Auto-write helper for tech-pay columns at job insert/edit time.
 *
 * SAFETY CONTRACT:
 *   • Hook-level flag gating: returns null when labor_engine_enabled OR
 *     ui_technician_pay is OFF. Caller MUST NOT include tech-pay columns
 *     in its payload when null is returned.
 *   • No assigned technician → returns null (no snapshot is written, ever).
 *     This is DISTINCT from `'no_config'`, which only applies when a tech
 *     IS assigned but has no compensation row.
 *   • Pay-side ONLY. Never read by scheduler / cascade / sell / cost engines.
 *   • R3 reference-time proxy: uses ticket_jobs.predicted_minutes as
 *     referenceMinutes — see computeJobTechPay.ts for the migration note.
 */

import { supabase } from '@/integrations/supabase/client';
import {
  computeJobTechPay,
  techPayColumnsForUpsert,
  type TechPayConfig,
  type TechPayMode,
} from '@/lib/labor/computeJobTechPay';

interface AutoWriteArgs {
  shopId: string;
  /** Assigned technician on the parent ticket. null → returns null. */
  technicianId: string | null | undefined;
  /** Reference minutes (R3: predicted_minutes / minutes_override fallback). */
  referenceMinutes: number;
  /** R2 sell snapshot (sold_minutes); pass null when not yet computed. */
  retailSoldMinutes: number | null;
  flags: {
    labor_engine_enabled: boolean;
    ui_technician_pay: boolean;
  };
}

/** Resolve current effective pay config row for a technician (manager-RLS gated). */
async function loadActiveTechPayConfig(
  shopId: string,
  technicianId: string,
): Promise<TechPayConfig | null> {
  const { data, error } = await (supabase as any)
    .from('technician_compensation')
    .select('pay_mode, hourly_rate, flat_rate_per_hour, effective_from, effective_to')
    .eq('shop_id', shopId)
    .eq('technician_id', technicianId)
    .is('effective_to', null)
    .order('effective_from', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return {
    pay_mode: (data.pay_mode as TechPayMode) ?? 'hourly',
    hourly_rate: data.hourly_rate != null ? Number(data.hourly_rate) : null,
    flat_rate_per_hour:
      data.flat_rate_per_hour != null ? Number(data.flat_rate_per_hour) : null,
  };
}

export async function buildTechPayColumnsForAutoWrite(
  args: AutoWriteArgs,
): Promise<ReturnType<typeof techPayColumnsForUpsert> | null> {
  if (!args.flags.labor_engine_enabled || !args.flags.ui_technician_pay) {
    return null;
  }
  // No assigned tech → write nothing. Distinct from `no_config`.
  if (!args.technicianId) return null;

  // Manager-only RLS will return null for non-managers; that's also a "no write".
  const techPayConfig = await loadActiveTechPayConfig(args.shopId, args.technicianId);

  const computed = computeJobTechPay({
    referenceMinutes: args.referenceMinutes,
    retailSoldMinutes: args.retailSoldMinutes,
    techPayConfig,
  });
  return techPayColumnsForUpsert(computed);
}

/**
 * Phase 10 R3 — Refresh tech-pay on an existing ticket_jobs row after its
 * reference minutes changed (e.g. user edited hours via handleEditJob).
 *
 * Behavior (mirrors R2 refreshLaborForRowEdit):
 *   • Flags OFF → no-op.
 *   • No assigned technician on the ticket → no-op.
 *   • Row has tech_pay_status IS NULL → no-op (refresh-only never CREATES a
 *     snapshot; the strict "no retroactive mutation" rule).
 *   • Row has any computed snapshot state ('computed' | 'deferred_hourly' |
 *     'no_config') → recomputes against current pay config + new reference
 *     minutes. Status may transition (e.g. tech reassignment is deferred so
 *     this normally only sees the same tech with the same config — shifts
 *     show only when reference/sold minutes change).
 */
export async function refreshTechPayForRowEdit(args: {
  ticketId: string;
  jobId: string;
  shopId: string;
  /** New reference-time proxy after the edit (predicted_minutes for the row). */
  referenceMinutes: number;
  flags: {
    labor_engine_enabled: boolean;
    ui_technician_pay: boolean;
  };
}): Promise<void> {
  if (!args.flags.labor_engine_enabled || !args.flags.ui_technician_pay) return;

  // Read row state + current sold_minutes + parent ticket's assigned tech.
  const { data: row, error: rowErr } = await (supabase as any)
    .from('ticket_jobs')
    .select(
      `tech_pay_status, sold_minutes,
       tickets:ticket_id(assigned_tech_id)`,
    )
    .eq('ticket_id', args.ticketId)
    .eq('job_id', args.jobId)
    .maybeSingle();
  if (rowErr || !row) return;

  // Strict "no retroactive mutation" — refresh never creates from null.
  if (row.tech_pay_status == null) return;

  const technicianId = (row.tickets as any)?.assigned_tech_id ?? null;
  if (!technicianId) return; // no-op: tech reassignment recompute is deferred

  const techPayConfig = await loadActiveTechPayConfig(args.shopId, technicianId);

  const computed = computeJobTechPay({
    referenceMinutes: args.referenceMinutes,
    retailSoldMinutes: row.sold_minutes != null ? Number(row.sold_minutes) : null,
    techPayConfig,
  });

  await (supabase as any)
    .from('ticket_jobs')
    .update(techPayColumnsForUpsert(computed))
    .eq('ticket_id', args.ticketId)
    .eq('job_id', args.jobId);
}
